import { useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { useUpdateAdminFeedback, type AdminFeedbackRow, type FeedbackStatus } from './api';
import { DASH, formatTimestamp } from './shared';

interface FeedbackDrawerProps {
  item: AdminFeedbackRow | null;
  onClose: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  FEATURE_REQUEST: 'Feature',
  BUG_REPORT: 'Bug',
  FEEDBACK: 'Feedback',
  FRICTION: 'Friction',
};

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  NEW: 'New',
  ACCEPTED: 'Accepted',
  IN_PROGRESS: 'In progress',
  FIXED: 'Fixed',
  IGNORED: 'Ignored',
};

const STATUS_CLASS: Record<FeedbackStatus, string> = {
  NEW: 'border-amber-500/50 text-amber-600 dark:text-amber-400',
  ACCEPTED: 'border-blue-500/50 text-blue-600 dark:text-blue-400',
  IN_PROGRESS: 'border-purple-500/50 text-purple-600 dark:text-purple-400',
  FIXED: 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
  IGNORED: 'border-muted text-muted-foreground',
};

const STATUS_OPTIONS: FeedbackStatus[] = ['NEW', 'ACCEPTED', 'IN_PROGRESS', 'FIXED', 'IGNORED'];

/** Rows predating the triage migration have no status; render them as New. */
export function FeedbackStatusBadge({ status }: { status: FeedbackStatus | undefined }) {
  const effective = status ?? 'NEW';
  return (
    <Badge variant="outline" className={STATUS_CLASS[effective]}>
      {STATUS_LABEL[effective]}
    </Badge>
  );
}

export function FeedbackDrawer({ item, onClose }: FeedbackDrawerProps) {
  const open = !!item;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="flex flex-col gap-0 overflow-y-auto">
        <SheetHeader className="space-y-1">
          <SheetTitle>{item?.title ?? 'Feedback'}</SheetTitle>
          <SheetDescription>
            {item ? <span className="font-mono text-xs">{item.id}</span> : null}
          </SheetDescription>
        </SheetHeader>

        {item ? (
          <div className="space-y-6 py-4">
            <Section title="Meta">
              <Row
                label="Type"
                value={<Badge variant="outline">{TYPE_LABEL[item.type] ?? item.type}</Badge>}
              />
              <Row label="Status" value={<FeedbackStatusBadge status={item.status} />} />
              <Row label="Agent" value={item.agent_name ?? DASH} />
              <Row
                label="Wallet"
                value={
                  <code className="text-xs">
                    {item.agent_wallet.slice(0, 6)}…{item.agent_wallet.slice(-4)}
                  </code>
                }
              />
              <Row label="Rating" value={item.rating == null ? DASH : '★'.repeat(item.rating)} />
              <Row label="Created at" value={formatTimestamp(item.created_at)} />
              {item.triaged_at ? (
                <Row
                  label="Triaged"
                  value={`${formatTimestamp(item.triaged_at)}${item.triaged_by ? ` by ${item.triaged_by}` : ''}`}
                />
              ) : null}
            </Section>

            <TriageSection key={item.id} item={item} />

            <Section title="Body">
              <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
                {item.body}
              </pre>
            </Section>

            {item.metadata ? (
              <Section title="Metadata">
                <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
                  {JSON.stringify(item.metadata, null, 2)}
                </pre>
              </Section>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/** Keyed by feedback id so local edit state resets when another row opens. */
function TriageSection({ item }: { item: AdminFeedbackRow }) {
  const update = useUpdateAdminFeedback();
  const [status, setStatus] = useState<FeedbackStatus>(item.status ?? 'NEW');
  const [notes, setNotes] = useState(item.triage_notes ?? '');
  const [githubUrl, setGithubUrl] = useState(item.github_url ?? '');

  return (
    <Section title="Triage">
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">Status</span>
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
          value={status}
          onChange={(e) => setStatus(e.target.value as FeedbackStatus)}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        placeholder="Triage notes — why was this accepted/ignored?"
        className="w-full rounded-md border bg-background p-2 text-sm"
      />
      <input
        value={githubUrl}
        onChange={(e) => setGithubUrl(e.target.value)}
        placeholder="https://github.com/... (fixing PR)"
        className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
      />
      {item.github_url ? (
        <a
          href={item.github_url}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-xs text-primary underline underline-offset-2"
        >
          {item.github_url}
        </a>
      ) : null}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="sm"
          disabled={update.isPending}
          onClick={() =>
            update.mutate({
              id: item.id,
              patch: {
                status,
                triage_notes: notes.trim() || null,
                github_url: githubUrl.trim() || null,
              },
            })
          }
        >
          {update.isPending ? 'Saving…' : 'Save triage'}
        </Button>
        {update.isError ? (
          <span className="text-xs text-destructive">
            {update.error instanceof Error ? update.error.message : 'Save failed'}
          </span>
        ) : null}
        {update.isSuccess && !update.isPending ? (
          <span className="text-xs text-muted-foreground">Saved</span>
        ) : null}
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
